
import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SVG, Svg, Image } from '@svgdotjs/svg.js';
import { Point } from 'app/controls/image-cropper/region';
import { ErrorDialogComponent
       } from 'app/dialogs/error-dialog/error-dialog.component';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { BoundingBox
       } from 'collage-designer/stage/placeholder/image-placeholder';
import { StageConstants } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { ImageStoreService
       } from 'core/imagestore/imagestore.service';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitter } from 'core/utils/async-event-emitter';
import { ImageProvider } from './imageprovider';

export interface GalleryImage
{
  readonly url: string;
  traudlUrls: {[pipeline: string]: string};
  readonly width: number;
  readonly height: number;
  readonly id: string;
  readonly hash?: string;
  readonly defaultCrop?: BoundingBox;
  contentCenter: Point | undefined;
  readonly expirationDate?: Date;
}

class DesignerExpiredError extends TrivialError
{
  // i18n.error.designerExpiredError.title
  // i18n.error.designerExpiredError.message
  protected _name: string = 'DesignerExpiredError';
}

@Injectable()
export class GalleryService implements OnDestroy
{
  public newImageAvailable: AsyncEventEmitter<GalleryImage> =
      new AsyncEventEmitter();
  public imageStockChanged: EventEmitter<boolean> = new EventEmitter();
  public uploadProgressChanged: AsyncEventEmitter<boolean> =
      new AsyncEventEmitter();
  public uploadFailed: EventEmitter<Svg> = new EventEmitter();

  private images: Array<GalleryImage> = [];
  private draggingImageId: string|undefined = undefined;
  private usedImages: {[designStateId: string]: Array<string>} = {};
  private selectedImageId: string|undefined = undefined;
  private imageExpiredWarningId?: number = undefined;
  private _uploadInProgress: boolean = false;

  public lastVisitedPage: number = 1;
  public imagesUploaded: number = 0;
  public imagesToUpload: number = 0;
  public uploadProgress: number = 0;

  constructor(
      private dialog: MatDialog,
      private loggingService: LoggingService,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(ErrorService) private errorService: ErrorService,
      @Inject(ImageStoreService) private imageStoreService: ImageStoreService,
  )
  { }

  public ngOnDestroy(): void
  {
    this.clearImageExpiredWarning();
  }

  private clearImageExpiredWarning(): void
  {
    if (this.imageExpiredWarningId) {
      clearInterval(this.imageExpiredWarningId);
      this.imageExpiredWarningId = undefined;
    }
  }

  private async imageExpiredWarning(): Promise<void>
  {
    const filtered = this.images.filter(i => i.expirationDate);
    if (filtered.length < 1) {
      this.clearImageExpiredWarning();
      return;
    }

    let info = undefined;
    try {
      info = await this.imageStoreService.getImageInfo(filtered[0].url);
    }
    catch (error) {
      if (error.status && error.status === 404) {
        this.loggingService.log(
            'GalleryService',
            Severity.NOTICE,
            'DesignerExpiredError because of expired image');
        this.dialog.open(ErrorDialogComponent, {
          data: {
            errors: [ new DesignerExpiredError() ],
          },
          panelClass: 'dialog-pane',
          disableClose: true,
        });
      }
      else {
        this.loggingService.log(
            'GalleryService',
            Severity.NOTICE,
            'failed to reload expiration: ' + error);
      }
    }

    if (info && info.expirationDate && info.expirationDate > new Date()) {
      this.setImageExpirationTimeout(info.expirationDate);
    }
    else {
      const fallbackExpirationDate = new Date();
      fallbackExpirationDate.setMinutes(
          fallbackExpirationDate.getMinutes() + 30);
      this.setImageExpirationTimeout(fallbackExpirationDate);
    }
  }

  public get uploadPercentage(): number
  {
    if (this.imagesToUpload < 1) {
      return 0;
    }

    return ((this.imagesUploaded + this.uploadProgress)
            / this.imagesToUpload
            * 100);
  }

  private setImageExpirationTimeout(imageExpiration: Date): void
  {
    // Maximum timeout for setTimeout is 24.8 days
    const maxExpirationPeriod = 2073600000; /* 24d */
    this.clearImageExpiredWarning();

    const timeout = imageExpiration.getTime() - new Date().getTime();

    if (timeout > 0 && timeout < maxExpirationPeriod) {
      this.imageExpiredWarningId = setTimeout(
          this.imageExpiredWarning.bind(this),
          timeout);
    }
  }

  public async addImage(info: GalleryImage): Promise<void>
  {
    // identische Bilder nicht mehrfach hinzufügen
    if (info === undefined ||
        info.id === undefined ||
        this.getImageById(info.id) !== undefined)
    {
      return;
    }

    this.images.push(info);

    if (!this.imageExpiredWarningId && info.expirationDate) {
      this.setImageExpirationTimeout(info.expirationDate);
    }
    await this.newImageAvailable.emit(info);
    this.imageStockChanged.emit();
  }

  public async addPendingImage(
    provider: ImageProvider, target?: Image, bbox?: BoundingBox):
      Promise<GalleryImage>
  {
    const id = this.getRandomGalleryId();

    if (target) {
      target.attr(StageConstants.ATTRIBUTE_PENDING, id);
    }

    const provided = await provider(id);
    const info = Object.assign({defaultCrop: bbox}, provided) as GalleryImage;

    await this.addImage(info);
    return info;
  }

  public getImages(unusedOnly: boolean = false): Array<GalleryImage>
  {
    if (unusedOnly) {
      return this.images.filter((image) => {
        return !this.isImageUsed(image.id);
      });
    }
    else {
      return this.images;
    }
  }

  public getImageById(imageId: string): GalleryImage
  {
    for (let image of this.images) {
      if (image.id === imageId) {
        return image;
      }
    }
    return undefined;
  }

  public setDraggingImageId(draggingImageId: string|undefined): void
  {
    this.draggingImageId = draggingImageId;
  }

  public getDraggingImageId(): string|undefined
  {
    return this.draggingImageId;
  }

  public isImageUsed(imageId: string): boolean
  {
    let usedImages = this.usedImages[this.getGalleryImagesStoreHash()];
    if (usedImages === undefined) {
      return false;
    }
    return usedImages.indexOf(imageId) >= 0;
  }

  public isImageSelected(imageId: string): boolean
  {
    if (this.selectedImageId === imageId && !this.isImageUsed(imageId)) {
      return true;
    }
    return false;
  }

  public getSelectedImageId(): string|undefined
  {
    return this.selectedImageId;
  }

  public setSelectedImageId(imageId: string|undefined): void
  {
    this.selectedImageId = imageId;
  }

  public setImageAsUsed(imageId: string): void
  {
    let usedImages = this.usedImages[this.getGalleryImagesStoreHash()];
    if (usedImages === undefined) {
      this.usedImages[this.getGalleryImagesStoreHash()] = [];
    }
    this.usedImages[this.getGalleryImagesStoreHash()].push(imageId);
  }

  public setImageAsUnused(imageId: string): void
  {
    let usedImages = this.usedImages[this.getGalleryImagesStoreHash()];

    let index = usedImages.indexOf(imageId);
    if (index > -1) {
      this.usedImages[this.getGalleryImagesStoreHash()].splice(index, 1);
    }
  }

  public setImagesAsUnused(): void
  {
    this.usedImages[this.getGalleryImagesStoreHash()] = [];
  }

  public removeImage(imageId: string): void
  {
    this.setImageAsUnused(imageId);
    this.images = this.images.filter((image) => {
      return image.id !== imageId;
    });
    this.imageStockChanged.emit();
  }

  public async beginUploadImages(): Promise<void>
  {
    await this.setUploadInProgress(true);
  }

  public async finishUploadImages(): Promise<void>
  {
    this.errorService.flush('GalleryService');
    if (this.imagesToUpload <= this.imagesUploaded) {
      await this.setUploadInProgress(false);
      this.imagesToUpload = 0;
      this.imagesUploaded = 0;
    }
  }

  public imageUploadFailed(error: TrivialError, target?: any): void
  {
    if (target) {
      this.uploadFailed.emit(target.parent());
    }
    this.errorService.add(
        error, 'GalleryService', !(error.name === 'UnsupportedMediaTypeError'));
  }

  public flushFailedUploads(): void
  {
    this.errorService.flushByType('GalleryService');
  }

  public getUploadInProgress(): boolean
  {
    return this._uploadInProgress;
  }

  public async setUploadInProgress(progress: boolean): Promise<void>
  {
    this._uploadInProgress = progress;
    await this.uploadProgressChanged.emit(progress);
  }

  private getGalleryImagesStoreHash(): string
  {
    let currentDesignState = this.designStepService.getCurrentDesignState();
    return [
        currentDesignState.designStepId,
        currentDesignState.partId
    ].join('-');
  }

  private getRandomGalleryId(): string
  {
    return 'gi' + Utils.getRandomId(24);
  }
}
